import { Link, useLocation } from 'react-router';
import type { AppRole } from '@infras/shared-types';
import { useCrumbTail } from '../lib/crumb';
// The scanner-freshness pill used to live here. Removed on request: it put a
// scanner's failure in the chrome of every page, where it read as an alarm about
// the page you were on. Scheduler still owns that signal (and keeps its dot in the
// sidebar), which is where someone goes to act on it.
import { GlobalSearchBar } from './GlobalSearchBar';
import { AccountMenu } from './AccountMenu';
import { Logo } from './Logo';

/**
 * Route → breadcrumb. Two levels: the nav GROUP, then the page. Longest prefix
 * wins, so `/repos/123` inherits `['Inventory','Repos']` and the entity name is
 * appended from the crumb context (see `lib/crumb`).
 *
 * Mirrors the sidebar's grouping on purpose — if a page's crumb group and its
 * nav group disagree, one of the two is wrong.
 */
const CRUMBS: [string, [string, string]][] = [
  // Search spans every doc type, but everything it returns lives under Inventory,
  // so it keeps that group rather than inventing a sixth.
  ['/overview', ['Inventory', 'Overview']],
  ['/search', ['Inventory', 'Search']],
  ['/repos', ['Inventory', 'Repos']],
  ['/clusters', ['Inventory', 'Clusters']],
  ['/secrets', ['Inventory', 'Secrets']],
  ['/graph', ['Inventory', 'Repos']],
  ['/scheduler', ['Operations', 'Scheduler']],
  ['/activity', ['Operations', 'Activity']],
  ['/access', ['Operations', 'Access requests']],
  ['/atlas-whitelist', ['Operations', 'Atlas IPs']],
  ['/agents', ['Operations', 'Agents']],
  ['/catalog', ['Reference', 'Deploy catalog']],
  ['/error-codes', ['Reference', 'Error dictionary']],
  ['/docs', ['Reference', 'Docs & MCP']],
  ['/settings/team', ['Administration', 'Users & roles']],
  ['/settings/reviewers', ['Administration', 'Access reviewers']],
  ['/settings/questions', ['Administration', 'Request questions']],
  ['/settings/profile', ['Administration', 'Account']],
  ['/settings', ['Administration', 'Account']],
  ['/vpn/peers', ['Network', 'Peers']],
  ['/vpn/subnets', ['Network', 'Subnets']],
  ['/vpn/connection-audit', ['Network', 'Connection audit']],
];

function crumbFor(pathname: string): [string, string] {
  if (pathname === '/') return ['Inventory', 'Overview'];
  // longest prefix first so /settings/team beats /settings
  const hit = [...CRUMBS]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([p]) => pathname === p || pathname.startsWith(p + '/'));
  return hit ? hit[1] : ['Inventory', 'Overview'];
}

export function TopBar({
  email,
  role,
  onLogout,
  showInventory,
}: {
  email: string;
  role: AppRole;
  onLogout: () => void;
  showInventory: boolean;
}) {
  const { pathname } = useLocation();
  const [group, page] = crumbFor(pathname);
  const tail = useCrumbTail();

  return (
    <header className="topbar">
      <Link to="/" className="brand-link" aria-label="OptInfra home">
        <Logo />
      </Link>
      <nav className="crumb" aria-label="Breadcrumb">
        <span className="crumb-group">{group}</span>
        <span className="crumb-sep">/</span>
        <span className="crumb-page">{page}</span>
        {tail && (
          <>
            <span className="crumb-sep">/</span>
            <span className="crumb-tail">{tail}</span>
          </>
        )}
      </nav>
      <div className="spacer" />
      {showInventory && <GlobalSearchBar />}
      <AccountMenu email={email} role={role} onLogout={onLogout} />
    </header>
  );
}
